import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
  useClearOrderMirrorOutboxMutation,
  useOrderMirrorOutboxCountQuery,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    maxWidth: 640,
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    p: 2,
  } satisfies SxProps<Theme>,
};

/**
 * Admin panel to inspect and clear the local environment's capture outbox
 * (`order_mirror_outbox`). Rendered in both producer and consumer modes: on a
 * producer it purges the live capture buffer; on a consumer it wipes rows
 * carried in by a restored production database dump. Clearing affects only the
 * local environment and is gated behind a confirmation dialog.
 */
const OutboxMaintenancePanel = () => {
  const { data, refetch, error } = useOrderMirrorOutboxCountQuery({
    fetchPolicy: "network-only",
  });
  const [clearOrderMirrorOutbox, { loading: clearing }] =
    useClearOrderMirrorOutboxMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const count = data?.orderMirrorOutboxCount ?? 0;

  const handleClear = useCallback(async () => {
    setClearError(null);
    try {
      const result = await clearOrderMirrorOutbox();
      throwOnGraphqlErrors(result);
      setConfirmOpen(false);
      await refetch();
    } catch (mutationError) {
      setClearError(
        mutationError instanceof Error ? mutationError.message : "Clear failed",
      );
    }
  }, [clearOrderMirrorOutbox, refetch]);

  return (
    <Box sx={styles.panel}>
      <Stack alignItems="center" direction="row" spacing={1}>
        <Typography variant="h6">Capture outbox</Typography>
        <Button
          size="small"
          onClick={() => {
            void refetch();
          }}
        >
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2">
        Captured order rows in this environment&apos;s outbox:{" "}
        <strong>{count}</strong>. Clearing removes all rows from this
        environment.
      </Typography>
      {error !== undefined && <Alert severity="error">{error.message}</Alert>}
      {clearError !== null && <Alert severity="error">{clearError}</Alert>}
      <Box>
        <Button
          color="error"
          disabled={count === 0}
          variant="outlined"
          onClick={() => {
            setClearError(null);
            setConfirmOpen(true);
          }}
        >
          Clear outbox
        </Button>
      </Box>
      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
        }}
      >
        <DialogTitle>Clear capture outbox?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes all {count} captured order rows from this
            environment&apos;s outbox and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={clearing}
            variant="contained"
            onClick={() => {
              void handleClear();
            }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutboxMaintenancePanel;
